import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box, Typography, IconButton, Chip, Tooltip, Collapse,
    Divider, useTheme, Button, CircularProgress,
} from '@mui/material';
import {
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Hub as CorrelationIcon,
    Shield as ShieldIcon,
    Schedule as ScheduleIcon,
    Layers as LayersIcon,
    DoNotDisturb as DismissIcon,
    OpenInNew as EscalateIcon,
    Computer as ComputerIcon,
    Person as PersonIcon,
    Router as RouterIcon,
    Security as MitreIcon,
    CheckCircleOutline as ActionIcon,
    LinkOff as RemoveLinkIcon,
} from '@mui/icons-material';
import { CorrelationDetail, CorrelationEntity } from '../types';
import { clearSelectedCorrelation, updateCorrelationStatus, fetchCorrelationById } from '../store/slices/correlationsSlice';
import { CorrelationsService } from '../services/correlationsService';
import { getSeverityChipSx } from '../theme/mappers/ticketMappers';
import { getRelativeTimeString } from '../utils/dateUtils';
import { colorUtils } from '../theme';

const ROLE_COLORS: Record<string, string> = {
    indicator: '#ef4444',
    actor: '#f97316',
    target: '#3b82f6',
    victim: '#3b82f6',
};

const SEVERITY_ORDER: Record<string, number> = {
    critical: 4, high: 3, medium: 2, low: 1,
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const CollapsibleSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    badge?: string | number;
    children: React.ReactNode;
    defaultExpanded?: boolean;
}> = ({ title, icon, badge, children, defaultExpanded = true }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const theme = useTheme();

    return (
        <Box sx={{ mb: 2 }}>
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 1,
                    cursor: 'pointer',
                    '&:hover .section-icon': { color: theme.palette.ui.iconActive },
                }}
            >
                <Box
                    className="section-icon"
                    sx={{
                        color: expanded ? theme.palette.ui.iconActive : theme.palette.ui.iconDefault,
                        display: 'flex',
                        transition: 'color 0.2s',
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="subtitle2" sx={{ flex: 1, color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.95rem' }}>
                    {title}
                </Typography>
                {badge !== undefined && (
                    <Typography variant="caption" sx={{
                        color: theme.palette.text.tertiary,
                        fontWeight: 600,
                        mr: 1,
                        fontSize: '0.85rem',
                        fontFamily: '"JetBrains Mono", monospace',
                    }}>
                        {badge}
                    </Typography>
                )}
                <Box sx={{ color: theme.palette.text.tertiary }}>
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ pt: 1, pl: 4, pr: 1 }}>{children}</Box>
            </Collapse>
        </Box>
    );
};

const EntityBadge: React.FC<{ entity: CorrelationEntity }> = ({ entity }) => {
    const theme = useTheme();
    const color = ROLE_COLORS[entity.role?.toLowerCase()] ?? theme.palette.text.secondary;
    return (
        <Tooltip
            title={`${entity.role ?? 'unknown'} · ${entity.type}${entity.is_derived ? ' · subnet bucket, not an exact match' : ''}`}
            arrow
        >
            <Chip
                label={entity.is_derived ? `${entity.value} · SUBNET` : entity.value}
                size="small"
                sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    height: 25,
                    backgroundColor: `${color}18`,
                    color,
                    border: `1px ${entity.is_derived ? 'dashed' : 'solid'} ${color}44`,
                    '& .MuiChip-label': { px: 0.75 },
                }}
            />
        </Tooltip>
    );
};

const TimelineRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.25 }}>
            <Typography variant="caption" sx={{
                color: theme.palette.text.tertiary,
                fontSize: '0.69rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                minWidth: 64,
                pt: 0.25,
            }}>
                {label.toUpperCase()}
            </Typography>
            <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                backgroundColor: theme.palette.border.strong,
                flexShrink: 0,
                mt: 0.5,
            }} />
            <Box>
                <Typography variant="caption" sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    color: theme.palette.text.secondary,
                    fontSize: '0.83rem',
                    display: 'block',
                }}>
                    {new Date(value).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.75rem' }}>
                    {getRelativeTimeString(value)}
                </Typography>
            </Box>
        </Box>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface CorrelationDetailsProps {
    correlation: CorrelationDetail;
}

const CorrelationDetails: React.FC<CorrelationDetailsProps> = ({ correlation }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [removingAlertId, setRemovingAlertId] = useState<string | null>(null);
    const [removeError, setRemoveError] = useState<{ alertId: string; message: string } | null>(null);

    const getSeverityColor = (severity: string | null) => {
        switch (severity?.toLowerCase()) {
            case 'critical': return theme.palette.severity.critical;
            case 'high': return theme.palette.severity.high;
            case 'medium': return theme.palette.severity.medium;
            case 'low': return theme.palette.severity.low;
            default: return theme.palette.text.secondary;
        }
    };

    const sortedTimestamps = [...(correlation.detected_at || [])].sort();
    const firstSeen = sortedTimestamps[0];
    const lastSeen = sortedTimestamps[sortedTimestamps.length - 1];

    const sortedAlerts = [...(correlation.alert_summaries || [])].sort((a, b) => {
        return (SEVERITY_ORDER[b.severity?.toLowerCase() ?? ''] ?? 0) -
            (SEVERITY_ORDER[a.severity?.toLowerCase() ?? ''] ?? 0);
    });

    const handleDismiss = () => {
        dispatch(updateCorrelationStatus({ id: correlation.id, status: 'dismissed' }) as any);
        dispatch(clearSelectedCorrelation());
    };

    const handleEscalate = () => {
        dispatch(updateCorrelationStatus({ id: correlation.id, status: 'escalated' }) as any);
        dispatch(clearSelectedCorrelation());
    };

    const handleRemoveAlert = async (alertId: string) => {
        setRemovingAlertId(alertId);
        setRemoveError(null);
        try {
            const updated = await CorrelationsService.removeAlert(correlation.id, alertId);
            if (updated) {
                dispatch(fetchCorrelationById(correlation.id) as any);
            } else {
                // Fewer than 2 alerts remained — the correlation itself was deleted.
                dispatch(clearSelectedCorrelation());
            }
        } catch (e: any) {
            setRemoveError({ alertId, message: e.response?.data?.error || 'Failed to remove alert from correlation' });
        } finally {
            setRemovingAlertId(null);
        }
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: theme.palette.background.default }}>

            {/* ================================================================
                HEADER
            ================================================================ */}
            <Box sx={{ px: 4, py: 3.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
                {/* Top row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CorrelationIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                        }}>
                            CORRELATION CLUSTER
                        </Typography>
                        {correlation.severity_highest && (
                            <Chip
                                label={correlation.severity_highest.toUpperCase()}
                                size="small"
                                sx={getSeverityChipSx(getSeverityColor(correlation.severity_highest))}
                            />
                        )}
                    </Box>
                    <IconButton size="small" sx={{ p: 0.75 }} onClick={() => dispatch(clearSelectedCorrelation())}>
                        <CloseIcon sx={{ fontSize: 21 }} />
                    </IconButton>
                </Box>

                {/* Title */}
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.15rem', lineHeight: 1.4, mb: 2 }}>
                    {correlation.title}
                </Typography>

                {/* Metrics row */}
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2.5 }}>
                    <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.69rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}>
                            ALERTS
                        </Typography>
                        <Typography sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 700,
                            fontSize: '1.61rem',
                            lineHeight: 1,
                            fontFamily: '"JetBrains Mono", monospace',
                        }}>
                            {correlation.alert_count}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.69rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}>
                            SCORE
                        </Typography>
                        <Typography sx={{
                            color: correlation.correlation_score >= 3
                                ? theme.palette.error.main
                                : theme.palette.warning.main,
                            fontWeight: 700,
                            fontSize: '1.61rem',
                            lineHeight: 1,
                            fontFamily: '"JetBrains Mono", monospace',
                        }}>
                            {correlation.correlation_score}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.69rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}>
                            ENTITIES
                        </Typography>
                        <Typography sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 700,
                            fontSize: '1.61rem',
                            lineHeight: 1,
                            fontFamily: '"JetBrains Mono", monospace',
                        }}>
                            {correlation.entities.length}
                        </Typography>
                    </Box>
                    {correlation.client_name && (
                        <Box>
                            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.69rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}>
                                CLIENT
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '0.94rem' }}>
                                {correlation.client_name}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Actions */}
                {correlation.status === 'open' && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EscalateIcon sx={{ fontSize: '15px !important' }} />}
                            onClick={handleEscalate}
                            sx={{
                                height: 32, fontSize: '0.8rem', fontWeight: 700,
                                color: theme.palette.warning.main,
                                borderColor: `${theme.palette.warning.main}55`,
                                '&:hover': { backgroundColor: `${theme.palette.warning.main}15`, borderColor: theme.palette.warning.main },
                            }}
                        >
                            Escalate
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DismissIcon sx={{ fontSize: '15px !important' }} />}
                            onClick={handleDismiss}
                            sx={{
                                height: 32, fontSize: '0.8rem', fontWeight: 700,
                                color: theme.palette.text.disabled,
                                borderColor: `${theme.palette.text.disabled}44`,
                                '&:hover': { backgroundColor: `${theme.palette.error.main}15`, color: theme.palette.error.main, borderColor: `${theme.palette.error.main}55` },
                            }}
                        >
                            Dismiss
                        </Button>
                    </Box>
                )}
                {correlation.status !== 'open' && (
                    <Chip
                        label={correlation.status.toUpperCase()}
                        size="small"
                        sx={{
                            height: 25, fontSize: '0.75rem', fontWeight: 700,
                            color: correlation.status === 'escalated' ? theme.palette.warning.main : theme.palette.text.disabled,
                            backgroundColor: 'transparent',
                            border: `1px solid ${correlation.status === 'escalated' ? theme.palette.warning.main + '55' : theme.palette.divider}`,
                        }}
                    />
                )}
            </Box>

            {/* ================================================================
                BODY
            ================================================================ */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 4,
                py: 3,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.border.main, borderRadius: '3px' },
                '&::-webkit-scrollbar-thumb:hover': { backgroundColor: theme.palette.border.strong },
            }}>

                {/* Timeline */}
                {(firstSeen || lastSeen) && (
                    <CollapsibleSection title="TIMELINE" icon={<ScheduleIcon fontSize="small" />}>
                        <Box sx={{ pl: 0.5 }}>
                            {firstSeen && <TimelineRow label="First Alert" value={firstSeen} />}
                            {lastSeen && lastSeen !== firstSeen && <TimelineRow label="Latest Alert" value={lastSeen} />}
                            <TimelineRow label="Opened" value={correlation.created_at} />
                        </Box>
                    </CollapsibleSection>
                )}

                <Divider sx={{ my: 1, borderColor: theme.palette.border.light }} />

                {/* Shared Entities */}
                {correlation.entities.length > 0 && (
                    <CollapsibleSection
                        title="SHARED ENTITIES"
                        icon={<LayersIcon fontSize="small" />}
                        badge={correlation.entities.length}
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {correlation.entities.map((e, i) => (
                                <EntityBadge key={i} entity={e} />
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
                            {Object.entries(ROLE_COLORS).map(([role, color]) => {
                                const count = correlation.entities.filter(e => e.role?.toLowerCase() === role).length;
                                if (!count) return null;
                                return (
                                    <Box key={role} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: color }} />
                                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.75rem' }}>
                                            {count} {role}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </CollapsibleSection>
                )}

                <Divider sx={{ my: 1, borderColor: theme.palette.border.light }} />

                {/* Alerts in cluster */}
                <CollapsibleSection
                    title="ALERTS IN CLUSTER"
                    icon={<ShieldIcon fontSize="small" />}
                    badge={sortedAlerts.length}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {sortedAlerts.map((alert) => {
                            const sevColor = getSeverityColor(alert.severity);
                            return (
                                <Box
                                    key={alert.id}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        backgroundColor: theme.palette.background.tertiary,
                                        border: `1px solid ${theme.palette.border.light}`,
                                        transition: 'all 0.15s ease',
                                        '&:hover': { backgroundColor: theme.palette.interactive.hover, borderColor: theme.palette.border.main },
                                    }}
                                >
                                    {/* Severity + title */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                                        {alert.severity && (
                                            <Chip
                                                label={alert.severity.toUpperCase()}
                                                size="small"
                                                sx={{
                                                    ...getSeverityChipSx(sevColor),
                                                    flexShrink: 0,
                                                    height: 21,
                                                    fontSize: '0.67rem',
                                                }}
                                            />
                                        )}
                                        <Typography variant="body2" sx={{
                                            fontWeight: 500,
                                            fontSize: '0.92rem',
                                            lineHeight: 1.35,
                                            color: theme.palette.text.primary,
                                            flex: 1,
                                        }}>
                                            {alert.title}
                                        </Typography>
                                        <Tooltip title="Remove from this correlation — only works if this alert is linked purely by a subnet match, not an exact IOC">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={removingAlertId === alert.id}
                                                    onClick={() => handleRemoveAlert(alert.id)}
                                                    sx={{ p: 0.4, flexShrink: 0, color: theme.palette.text.disabled, '&:hover': { color: theme.palette.error.main } }}
                                                >
                                                    {removingAlertId === alert.id
                                                        ? <CircularProgress size={13} />
                                                        : <RemoveLinkIcon sx={{ fontSize: 17 }} />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>

                                    {removeError?.alertId === alert.id && (
                                        <Typography variant="caption" sx={{ color: theme.palette.error.main, display: 'block', mb: 0.75, fontSize: '0.78rem' }}>
                                            {removeError.message}
                                        </Typography>
                                    )}

                                    {/* Meta row — host, ip, user */}
                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
                                        {alert.hostname && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                <ComputerIcon sx={{ fontSize: 13, color: theme.palette.text.disabled }} />
                                                <Typography variant="caption" sx={{
                                                    fontFamily: '"JetBrains Mono", monospace',
                                                    fontSize: '0.75rem',
                                                    color: theme.palette.text.secondary,
                                                }}>
                                                    {alert.hostname}
                                                </Typography>
                                            </Box>
                                        )}
                                        {alert.ip_address && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                <RouterIcon sx={{ fontSize: 13, color: theme.palette.text.disabled }} />
                                                <Typography variant="caption" sx={{
                                                    fontFamily: '"JetBrains Mono", monospace',
                                                    fontSize: '0.75rem',
                                                    color: theme.palette.text.secondary,
                                                }}>
                                                    {alert.ip_address}
                                                </Typography>
                                            </Box>
                                        )}
                                        {alert.username && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                <PersonIcon sx={{ fontSize: 13, color: theme.palette.text.disabled }} />
                                                <Typography variant="caption" sx={{
                                                    fontFamily: '"JetBrains Mono", monospace',
                                                    fontSize: '0.75rem',
                                                    color: theme.palette.text.secondary,
                                                }}>
                                                    {alert.username}
                                                </Typography>
                                            </Box>
                                        )}
                                        {alert.integration_type && (
                                            <Typography variant="caption" sx={{
                                                fontSize: '0.71rem',
                                                color: theme.palette.text.disabled,
                                                textTransform: 'capitalize',
                                            }}>
                                                {alert.integration_type}
                                            </Typography>
                                        )}
                                        {alert.detected_at && (
                                            <Typography variant="caption" sx={{
                                                fontSize: '0.71rem',
                                                color: theme.palette.text.disabled,
                                                ml: 'auto',
                                            }}>
                                                {getRelativeTimeString(alert.detected_at)}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* MITRE + action row */}
                                    {(alert.mitre_tactic || alert.mitre_technique || alert.action_taken) && (
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
                                            {(alert.mitre_tactic || alert.mitre_technique) && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                    <MitreIcon sx={{ fontSize: 13, color: theme.palette.primary.main, opacity: 0.7 }} />
                                                    <Typography variant="caption" sx={{
                                                        fontSize: '0.71rem',
                                                        color: theme.palette.primary.main,
                                                        opacity: 0.85,
                                                        fontWeight: 600,
                                                    }}>
                                                        {[alert.mitre_tactic, alert.mitre_technique].filter(Boolean).join(' · ')}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {alert.action_taken && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                    <ActionIcon sx={{ fontSize: 13, color: theme.palette.success.main, opacity: 0.8 }} />
                                                    <Typography variant="caption" sx={{
                                                        fontSize: '0.71rem',
                                                        color: theme.palette.success.main,
                                                        opacity: 0.85,
                                                        textTransform: 'capitalize',
                                                    }}>
                                                        {alert.action_taken}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Ticket badge */}
                                    {alert.ticket_id && (
                                        <Box sx={{ mt: 0.75 }}>
                                            <Chip
                                                label={`Ticket · ${alert.ticket_id.slice(0, 8)}…`}
                                                size="small"
                                                sx={{
                                                    height: 21,
                                                    fontSize: '0.69rem',
                                                    fontWeight: 600,
                                                    fontFamily: '"JetBrains Mono", monospace',
                                                    backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.08),
                                                    color: theme.palette.primary.main,
                                                    border: `1px solid ${colorUtils.withOpacity(theme.palette.primary.main, 0.25)}`,
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </CollapsibleSection>
            </Box>
        </Box>
    );
};

export default CorrelationDetails;